import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import BodyText from 'LuxKit/Typography/BodyText'
import Divider from 'LuxKit/Divider'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getCruiseDepartureCabinPrices } from 'lib/cruises/cruiseDeparturePricing'
import CruiseOfferPageDepartureCabinPriceItem from './CruiseOfferPageDepartureCabinPriceItem'

const PricesGrid = styled.div<{ $categoryCount: number }>`
  display: grid;
  grid-template-columns: 1fr;

  ${mediaQueryUp.desktop} {
    gap: ${rem(20)};
    grid-template-columns: repeat(${props => props.$categoryCount}, minmax(${rem(130)}, 1fr));
  }
`

const PriceItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  min-width: ${rem(130)};
`

interface Props {
  offer: App.CruiseOffer
  departure: App.CruiseDepartureDetails
  departureMemberPricingByCategory: Record<string, App.Cruises.CruiseOfferMemberPriceByCategory>
}

function CruiseDepartureTilePrices({
  offer,
  departure,
  departureMemberPricingByCategory,
}: Props) {
  const departurePrices = useMemo(() => {
    return getCruiseDepartureCabinPrices(offer, departure, departureMemberPricingByCategory)
  }, [departure, departureMemberPricingByCategory, offer])

  return <PricesGrid $categoryCount={departurePrices.length}>
    {departurePrices.map((price, index) => {
      return <PriceItem key={price.category}>
        <CruiseOfferPageDepartureCabinPriceItem
          offer={offer}
          departure={departure}
          price={price}
        />
        {index !== departurePrices.length - 1 && <CSSBreakpoint only="mobile">
          <Divider kind="primary" />
        </CSSBreakpoint>}
      </PriceItem>
    })}

    {departurePrices.length === 0 && <Group direction="horizontal" horizontalAlign="center">
      <BodyText variant="medium" weight="normal" colour="light-foreground-alt">
        <FormattedMessage
          id="MS-KJ65nJRQALFaMLLgk"
          defaultMessage="No prices available"
          description="Empty state message shown when no cabin prices are available for a cruise departure"
        />
      </BodyText>
    </Group>}
  </PricesGrid>
}

export default CruiseDepartureTilePrices
